import { z } from "zod";
//#region src/onboarding/onboarding-state.ts
/**
* The states of a tenant onboarding job, its failure codes and its intake refusal codes.
*
* Plan `2026-10-16-customer-onboarding`, workstream WS-1, decisions D1, D7 and D30, default F5,
* rulings R-055 and R-070. Plan `2026-11-04-cp-onboard-idempotency`, workstream WS-1, decision D10:
* the refusal code `REQUEST_KEY_REUSED`.
*
* The job runs one step at a time: tenant, then cloud base, then data plane, then licences, then
* the wait for a client secret that follows the intake, then org seed (overview section 3, R-055
* item 2). Each step state names the step that runs now, the shape of the
* provisioning workflow, where a state names its entry command. A failed step moves the job to
* `failed` and records the step in `failedInState`. A retry enters that step again, the rule of
* the provisioning workflow guard `canRetry`.
*
* Who uses it:
*
* - provisioning-svc (WS-6) writes the state, the failure code and the refusal codes.
* - The console progress pane (WS-16), site-admin-api (WS-17) and `zion-ops` (WS-18) read them.
*
* Every function here is pure and never throws.
*/
/** The steps of the job, in the order the job runs them. */
const ONBOARDING_STEP_STATES = [
	"creating_tenant",
	"creating_cloud_base",
	"creating_data_plane",
	"granting_licences",
	"awaiting_client_secret",
	"seeding_org"
];
/**
* Every state of the job.
*
* - `pending`: provisioning-svc accepted the request and no step has started.
* - the six step states of {@link ONBOARDING_STEP_STATES}: that step runs now.
* - `awaiting_client_secret`: the request carries the marker `clientSecretLater: true` and the job
*   found no client secret for it. provisioning-svc enters this state after the licence grant, when
*   the marker is set and its vault read finds no value, or when that vault read fails (R-073 item
*   3). In the second case the vault can hold a value. The job leaves it for `seeding_org`
*   when an operator sends the secret, or fails with `CLIENT_SECRET_NOT_PROVIDED` after 7 days. The
*   job keeps its tenant slug and its data plane name reserved while it waits (R-055 item 2).
* - `ready`: every step finished. The org is seeded and its licences are granted.
* - `failed`: one step failed. The job waits for a retry.
*/
const ONBOARDING_STATES = [
	"pending",
	...ONBOARDING_STEP_STATES,
	"ready",
	"failed"
];
/** Runtime validator for {@link ONBOARDING_STATES}. */
const onboardingStateSchema = z.enum(ONBOARDING_STATES);
/** Runtime validator for {@link ONBOARDING_STEP_STATES}. */
const onboardingStepStateSchema = z.enum(ONBOARDING_STEP_STATES);
/** Tell whether a state is one of the six step states. `pending`, `ready` and `failed` are not. */
function isOnboardingStepState(state) {
	return ONBOARDING_STEP_STATES.includes(state);
}
/**
* The state that follows a state when its step finishes.
*
* - `pending` gives `creating_tenant`.
* - each step state gives the next step state in {@link ONBOARDING_STEP_STATES}, so
*   `granting_licences` gives `awaiting_client_secret`, and `seeding_org` gives `ready`. A job with
*   no marker passes over the wait: provisioning-svc moves it from `granting_licences` to
*   `seeding_org` itself (R-071 item 1).
* - `ready` and `failed` give `null`: no step follows. A retry leaves `failed` through
*   `failedInState`, never through this function.
*/
function nextOnboardingState(state) {
	if (state === "ready" || state === "failed") return null;
	return ONBOARDING_STATES[ONBOARDING_STATES.indexOf(state) + 1];
}
/** The failure codes of a failed job, in declaration order. {@link ONBOARDING_FAILURE_CODES} gives the meaning of each. */
const ONBOARDING_FAILURE_CODE_NAMES = [
	"TENANT_CREATE_FAILED",
	"TENANT_SLUG_TAKEN",
	"CLOUD_BASE_FAILED",
	"CLOUD_BASE_TIMEOUT",
	"DATA_PLANE_CREATE_FAILED",
	"DATA_PLANE_FAILED",
	"DATA_PLANE_TIMEOUT",
	"LICENCE_GRANT_FAILED",
	"CLIENT_SECRET_NOT_PROVIDED",
	"SECRET_HANDOFF_FAILED",
	"ORG_SEED_FAILED",
	"ORG_SEED_TIMEOUT",
	"REQUEST_INTERRUPTED"
];
/**
* The failure codes of a failed job, each with the step states it can come from.
*
* - `TENANT_CREATE_FAILED`: platform-api refused or did not answer the tenant create.
* - `TENANT_SLUG_TAKEN`: another tenant took the slug after the intake check.
* - `CLOUD_BASE_FAILED`: the `TenantCloudBase` XR reported `Synced=False`.
* - `CLOUD_BASE_TIMEOUT`: the `TenantCloudBase` XR did not reach `Ready` in time.
* - `DATA_PLANE_CREATE_FAILED`: the data plane create refused the request.
* - `DATA_PLANE_FAILED`: the provisioning job of the data plane reached its `FAILED` state.
* - `DATA_PLANE_TIMEOUT`: the data plane did not reach `ACTIVE` in time.
* - `LICENCE_GRANT_FAILED`: release-engine refused a licence grant.
* - `CLIENT_SECRET_NOT_PROVIDED`: no operator sent the client secret within 7 days of the entry to
*   `awaiting_client_secret` (R-055 item 2). A retry returns the job to the wait.
* - `SECRET_HANDOFF_FAILED`: the write of the client secret to the data plane failed (D12).
* - `ORG_SEED_FAILED`: the data plane reported a refused org seed.
* - `ORG_SEED_TIMEOUT`: the data plane did not report the org seed in time.
* - `REQUEST_INTERRUPTED`: a restart of provisioning-svc found the step with no record of its
*   outcome. It can come from any step.
*/
const ONBOARDING_FAILURE_CODES = Object.freeze({
	TENANT_CREATE_FAILED: ["creating_tenant"],
	TENANT_SLUG_TAKEN: ["creating_tenant"],
	CLOUD_BASE_FAILED: ["creating_cloud_base"],
	CLOUD_BASE_TIMEOUT: ["creating_cloud_base"],
	DATA_PLANE_CREATE_FAILED: ["creating_data_plane"],
	DATA_PLANE_FAILED: ["creating_data_plane"],
	DATA_PLANE_TIMEOUT: ["creating_data_plane"],
	LICENCE_GRANT_FAILED: ["granting_licences"],
	CLIENT_SECRET_NOT_PROVIDED: ["awaiting_client_secret"],
	SECRET_HANDOFF_FAILED: ["seeding_org"],
	ORG_SEED_FAILED: ["seeding_org"],
	ORG_SEED_TIMEOUT: ["seeding_org"],
	REQUEST_INTERRUPTED: ONBOARDING_STEP_STATES
});
/** Runtime validator for {@link ONBOARDING_FAILURE_CODE_NAMES}. */
const onboardingFailureCodeSchema = z.enum(ONBOARDING_FAILURE_CODE_NAMES);
/** The step states a failure code can come from. */
function onboardingFailureCodeSteps(code) {
	return ONBOARDING_FAILURE_CODES[code];
}
/**
* Tell whether `POST /v1/onboarding-requests/:id/retry` may restart a job.
*
* Returns `true` only for a `failed` job that names the step it failed in. Every other job answers
* `false`, and the route refuses it with `REQUEST_NOT_RETRYABLE`.
*/
function canRetryOnboarding(job) {
	return job.state === "failed" && job.failedInState !== null;
}
/**
* The codes of a refusal that provisioning-svc answers before it stores a request, or on a retry.
*
* - `TENANT_SLUG_TAKEN` (409): a tenant has the slug.
* - `TENANT_SLUG_RESERVED` (400): platform-api reserves the slug.
* - `DATA_PLANE_NAME_TAKEN` (409): a live data plane has the name.
* - `DATA_PLANE_NAME_RESERVED` (400): provisioning-svc reserves the name.
* - `REGION_NOT_ALLOWED` (400): the region is not in the allowed list of the cloud.
* - `CLOUD_NOT_ENABLED` (400): this CP cannot build a data plane on the cloud.
* - `APP_NOT_FOUND` (400): no app has a slug of the request.
* - `REQUEST_NOT_RETRYABLE` (409): {@link canRetryOnboarding} answered `false`.
* - `CLIENT_SECRET_NOT_ACCEPTED` (409): `PUT /v1/onboarding-requests/:id/client-secret` refused
*   the paste. The sign-in type takes no client secret, or the job is past the point where a secret
*   can still reach the org seed (R-070 item 4, R-071 item 2).
* - `REQUEST_KEY_REUSED` (409): the caller sent a request key that one of its own requests already
*   holds, and the hash of this body differs from the hash of that request (plan
*   2026-11-04-cp-onboard-idempotency, D4 and D10). provisioning-svc writes nothing for it: no vault
*   secret, no row, no audit row and no job kick. The message names no value of either body and
*   no hash.
*
* The list is closed and its order is fixed: a new code goes at the end, so a consumer that keys a
* map on {@link OnboardingRefusalCode} fails its typecheck until it maps the new code.
*/
const ONBOARDING_REFUSAL_CODES = [
	"TENANT_SLUG_TAKEN",
	"TENANT_SLUG_RESERVED",
	"DATA_PLANE_NAME_TAKEN",
	"DATA_PLANE_NAME_RESERVED",
	"REGION_NOT_ALLOWED",
	"CLOUD_NOT_ENABLED",
	"APP_NOT_FOUND",
	"REQUEST_NOT_RETRYABLE",
	"CLIENT_SECRET_NOT_ACCEPTED",
	"REQUEST_KEY_REUSED"
];
/** The longest refusal or failure message, in characters. */
const ONBOARDING_MESSAGE_MAX_LENGTH = 1024;
/** One line of 1 to 1024 characters with no control character. */
const onboardingMessageSchema = z.string().min(1).max(ONBOARDING_MESSAGE_MAX_LENGTH).regex(/^\P{Cc}+$/u, "must not contain a control character");
/**
* The body of an intake refusal: `{ code, message }`, the shape of the tenant project refusals of
* provisioning-svc (`TENANT_GCP_PROJECT_*`). Strict. The message names the rule and never echoes a
* secret (default F8). The code is one of {@link ONBOARDING_REFUSAL_CODES}, `REQUEST_KEY_REUSED`
* included; any other code fails the parse.
*/
const onboardingRefusalSchema = z.strictObject({
	code: z.enum(ONBOARDING_REFUSAL_CODES),
	message: onboardingMessageSchema
});
/** The states in which the tenant exists, so `tenantId` is set. */
const TENANT_SET_STATES = /* @__PURE__ */ new Set([
	"creating_cloud_base",
	"creating_data_plane",
	"granting_licences",
	"awaiting_client_secret",
	"seeding_org",
	"ready"
]);
/** The states in which the data plane exists, so `dataPlaneId` is set. */
const DATA_PLANE_SET_STATES = /* @__PURE__ */ new Set([
	"granting_licences",
	"awaiting_client_secret",
	"seeding_org",
	"ready"
]);
/**
* Find the first broken rule of a job's state fields, or `null` when all hold.
*
* Caller: the refinement of `onboardingRequestViewSchema`, and provisioning-svc before it writes a
* row. The rules:
* 1. A `failed` job has `failedInState`, `failureCode` and `failureMessage`; any other job has all
*    three `null`.
* 2. The failure code can come from `failedInState` ({@link onboardingFailureCodeSteps}).
* 3. A job past `creating_tenant` (and not `failed`) has `tenantId`.
* 4. A job past `creating_data_plane` (and not `failed`) has `dataPlaneId`.
* 5. A `pending` job has neither id.
*
* Output: the text of the first broken rule, in the order above.
*/
function onboardingProgressIssue(progress) {
	const failed = progress.state === "failed";
	const failureFields = [
		progress.failedInState,
		progress.failureCode,
		progress.failureMessage
	];
	if (failed && failureFields.includes(null)) return "a failed job names failedInState, failureCode and failureMessage";
	if (!failed && failureFields.some((field) => field !== null)) return "only a failed job names failedInState, failureCode or failureMessage";
	if (failed && !onboardingFailureCodeSteps(progress.failureCode).includes(progress.failedInState)) return "the failureCode cannot come from failedInState";
	if (TENANT_SET_STATES.has(progress.state) && progress.tenantId === null) return `a job in ${progress.state} names its tenantId`;
	if (DATA_PLANE_SET_STATES.has(progress.state) && progress.dataPlaneId === null) return `a job in ${progress.state} names its dataPlaneId`;
	if (progress.state === "pending" && (progress.tenantId !== null || progress.dataPlaneId !== null)) return "a pending job names no tenantId and no dataPlaneId";
	return null;
}
//#endregion
export { ONBOARDING_FAILURE_CODES, ONBOARDING_FAILURE_CODE_NAMES, ONBOARDING_MESSAGE_MAX_LENGTH, ONBOARDING_REFUSAL_CODES, ONBOARDING_STATES, ONBOARDING_STEP_STATES, canRetryOnboarding, isOnboardingStepState, nextOnboardingState, onboardingFailureCodeSchema, onboardingFailureCodeSteps, onboardingMessageSchema, onboardingProgressIssue, onboardingRefusalSchema, onboardingStateSchema, onboardingStepStateSchema };

//# sourceMappingURL=onboarding-state.js.map